import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles';
import {
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  ButtonBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  Box,
  Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import InstagramIcon from '@material-ui/icons/Instagram';
import FacebookIcon from '@material-ui/icons/Facebook';
import TelegramIcon from '@material-ui/icons/Telegram';
import ChatIcon from '@material-ui/icons/Chat';
import { getTagsList, getTags } from '../../services/tagService';
import { getTickets } from '../../services/ticketService';
import { AuthContext } from '../../context/Auth/AuthContext';
import toastError from '../../errors/toastError';

const useStyles = makeStyles((theme) => ({
  /** Papel + cabeçalho alinhados ao `QueueModal` (Departamentos). */
  paper: {
    borderRadius: 16,
    maxWidth: 560,
    width: '100%',
    maxHeight: '80vh',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.14)',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '95vw',
      margin: theme.spacing(1),
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  headerIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      fontSize: 22,
      color: theme.palette.primary.dark || theme.palette.primary.main,
    },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  closeButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#f1f5f9',
      color: '#334155',
    },
  },
  dialogContent: {
    padding: 0,
    backgroundColor: '#f8fafc',
  },
  searchContainer: {
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 2,
  },
  topInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  searchLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: '#475569',
  },
  resultCount: {
    height: 24,
    borderRadius: 999,
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    color: '#334155',
    fontWeight: 600,
    fontSize: 11,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#f8fafc',
      '&:hover': {
        backgroundColor: '#f1f5f9',
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dbe4ee',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchMetaText: {
    marginTop: theme.spacing(0.8),
    fontSize: 11,
    color: '#94a3b8',
  },
  searchModeToggle: {
    marginTop: theme.spacing(1),
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 999,
    display: 'inline-flex',
    padding: 2,
    gap: 2,
  },
  searchModeButton: {
    borderRadius: 999,
    padding: theme.spacing(0.5, 1.1),
    fontSize: 11,
    fontWeight: 600,
    color: '#64748b',
    transition: 'all 0.2s ease',
  },
  searchModeButtonActive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  searchStrong: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  tagsFilterWrap: {
    marginTop: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      minHeight: 40,
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cbd5e1',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  tagChip: {
    borderRadius: 999,
    fontWeight: 600,
    fontSize: 11,
    height: 22,
  },
  bodyContainer: {
    padding: theme.spacing(1.2, 1.5, 1.5),
  },
  resultsList: {
    padding: 0,
    maxHeight: 'calc(80vh - 230px)',
    overflowY: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    backgroundColor: '#fff',
  },
  listItem: {
    padding: 0,
  },
  listButton: {
    width: '100%',
    border: 0,
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    padding: theme.spacing(1.5, 1.8),
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  itemRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.4),
  },
  avatar: {
    width: 44,
    height: 44,
    backgroundColor: '#ede9fe',
    color: '#6d28d9',
    fontWeight: 700,
  },
  contentWrap: {
    flex: 1,
    minWidth: 0,
  },
  nameLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  contactName: {
    fontWeight: 700,
    fontSize: 15,
    color: '#0f172a',
    lineHeight: 1.2,
  },
  ticketId: {
    marginLeft: 'auto',
    color: '#94a3b8',
    fontSize: 11,
  },
  lastMessage: {
    fontSize: 13,
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 320,
    marginTop: 2,
  },
  ticketInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  statusChip: {
    height: 22,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    borderRadius: 999,
    letterSpacing: 0.2,
  },
  channelChip: {
    height: 20,
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    textTransform: 'capitalize',
  },
  channelIcon: {
    fontSize: 15,
    marginRight: 2,
  },
  whatsappIcon: {
    color: '#25d366',
  },
  instagramIcon: {
    color: '#e1306c',
  },
  facebookIcon: {
    color: '#1877f2',
  },
  telegramIcon: {
    color: '#229ED9',
  },
  noResults: {
    padding: 0,
  },
  loadingContainer: {
    padding: 0,
  },
  queueChip: {
    height: 22,
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 999,
  },
  searchHint: {
    padding: 0,
  },
  searchHintIcon: {
    fontSize: 44,
    color: '#cbd5e1',
    marginBottom: theme.spacing(1),
  },
  stateCard: {
    border: '1px dashed #cbd5e1',
    borderRadius: 14,
    backgroundColor: '#fff',
    textAlign: 'center',
    padding: theme.spacing(4.5, 3),
  },
  stateTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#334155',
  },
  stateText: {
    marginTop: theme.spacing(0.7),
    fontSize: 13,
    color: '#64748b',
  },
  loadingLabel: {
    marginTop: theme.spacing(1),
    fontSize: 13,
    color: '#64748b',
  },
  divider: {
    margin: 0,
    backgroundColor: '#edf2f7',
  },
  footerHint: {
    marginTop: theme.spacing(0.8),
    fontSize: 11,
    color: '#94a3b8',
  },
  highlightText: {
    backgroundColor: '#fef08a',
    borderRadius: 3,
    padding: '0 2px',
    fontWeight: 600,
    color: '#7c2d12',
  },
}));

const SearchTicketModal = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user: _user } = useContext(AuthContext);

  const [searchParam, setSearchParam] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState('contact');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (!open) {
      setSearchParam('');
      setTickets([]);
      setSearchMode('contact');
      setSelectedTags([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const loadTags = async () => {
      try {
        const { data } = await getTagsList();
        setTags(Array.isArray(data) ? data : []);
      } catch {
        try {
          const { data } = await getTags();
          const parsed = Array.isArray(data) ? data : data?.tags || [];
          setTags(Array.isArray(parsed) ? parsed : []);
        } catch {
          setTags([]);
        }
      }
    };

    loadTags();
  }, [open]);

  useEffect(() => {
    const hasTextSearch = searchParam.trim().length >= 2;
    const hasTagsFilter = selectedTags.length > 0;

    if (!hasTextSearch && !hasTagsFilter) {
      setTickets([]);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchTickets();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, searchMode, selectedTags]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const normalizedSearch = searchParam.trim();
      const hasTextSearch = normalizedSearch.length >= 2;

      const { data } = await getTickets({
        searchParam: normalizedSearch,
        pageNumber: 1,
        showAll: 'true',
        searchOnMessages: searchMode === 'message' ? 'true' : 'false',
        ...(selectedTags.length > 0
          ? { tags: JSON.stringify(selectedTags.map((tag) => tag.id)) }
          : {}),
      });

      const ticketsList = data.tickets || [];
      const search = normalizedSearch.toLowerCase();

      let sortedTickets = ticketsList;
      if (hasTextSearch) {
        // Filtrar e ordenar: tickets com match na mensagem/contato primeiro
        sortedTickets = ticketsList
          .map((ticket) => {
            const lastMessage = (ticket.lastMessage || '').toLowerCase();
            const contactName = (ticket.contact?.name || '').toLowerCase();
            const contactNumber = (ticket.contact?.number || '').toLowerCase();
            const ticketUserName = (ticket.user?.name || '').toLowerCase();

            let score = 0;
            if (searchMode === 'message') {
              if (lastMessage.includes(search)) score += 10;
              if (contactName.includes(search)) score += 2;
              if (contactNumber.includes(search)) score += 1;
            } else {
              if (contactName.includes(search)) score += 10;
              if (contactNumber.includes(search)) score += 8;
              if (ticketUserName.includes(search)) score += 6;
            }

            return { ...ticket, _score: score };
          })
          .filter((ticket) => ticket._score > 0)
          .sort((a, b) => b._score - a._score);
      }

      setTickets(sortedTickets);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = (ticket) => {
    onClose();
    history.push(`/tickets/conversation/${ticket.uuid}`);
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp':
        return <WhatsAppIcon className={`${classes.channelIcon} ${classes.whatsappIcon}`} />;
      case 'instagram':
        return <InstagramIcon className={`${classes.channelIcon} ${classes.instagramIcon}`} />;
      case 'facebook':
        return <FacebookIcon className={`${classes.channelIcon} ${classes.facebookIcon}`} />;
      case 'telegram':
        return <TelegramIcon className={`${classes.channelIcon} ${classes.telegramIcon}`} />;
      default:
        return <ChatIcon className={classes.channelIcon} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return { backgroundColor: '#dcfce7', color: '#166534' };
      case 'pending':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'closed':
        return { backgroundColor: '#f3f4f6', color: '#6b7280' };
      default:
        return { backgroundColor: '#e0e7ff', color: '#3730a3' };
    }
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'open':
        return '#bbf7d0';
      case 'pending':
        return '#fde68a';
      case 'closed':
        return '#e5e7eb';
      default:
        return '#c7d2fe';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return 'Aberto';
      case 'pending':
        return 'Pendente';
      case 'closed':
        return 'Resolvido';
      default:
        return status;
    }
  };

  const formatLastMessage = (ticket) => {
    if (ticket.lastMessage) {
      return ticket.lastMessage.length > 60
        ? ticket.lastMessage.substring(0, 60) + '...'
        : ticket.lastMessage;
    }
    return 'Sem mensagens';
  };

  // Função para destacar o texto buscado na mensagem
  const highlightSearchTerm = (text, search) => {
    if (!text || !search || search.length < 2) return text;

    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className={classes.highlightText}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Função para encontrar e mostrar o trecho da mensagem que contém o termo buscado
  const getMatchingMessagePreview = (ticket) => {
    const message = ticket.lastMessage || '';
    const search = searchParam.toLowerCase();

    if (!message || search.length < 2 || searchMode !== 'message') {
      return formatLastMessage(ticket);
    }

    const lowerMessage = message.toLowerCase();
    const index = lowerMessage.indexOf(search);

    if (index === -1) {
      return formatLastMessage(ticket);
    }

    // Mostrar contexto ao redor do termo encontrado
    const start = Math.max(0, index - 20);
    const end = Math.min(message.length, index + search.length + 40);

    let preview = message.substring(start, end);
    if (start > 0) preview = '...' + preview;
    if (end < message.length) preview = preview + '...';

    return highlightSearchTerm(preview, searchParam);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      PaperProps={{ className: classes.paper }}
      aria-labelledby="search-ticket-dialog-title"
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIconBox}>
            <SearchIcon />
          </Box>
          <Box minWidth={0}>
            <Typography
              id="search-ticket-dialog-title"
              component="h2"
              className={classes.headerTitle}
            >
              Buscar Conversas
            </Typography>
            <Typography className={classes.headerSubtitle}>
              Encontre por nome, número ou trecho da mensagem
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Fechar">
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={onClose}
            aria-label="Fechar"
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.searchContainer}>
          <div className={classes.topInfo}>
            <Typography className={classes.searchLabel}>Busca inteligente</Typography>
            {searchParam.length >= 2 && (
              <Chip
                size="small"
                label={`${tickets.length} resultado${tickets.length === 1 ? '' : 's'}`}
                className={classes.resultCount}
              />
            )}
          </div>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder={
              searchMode === 'message'
                ? 'Buscar no texto das mensagens...'
                : 'Buscar por contato (nome, numero ou atendente)...'
            }
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.searchModeToggle}>
            <ButtonBase
              className={`${classes.searchModeButton} ${
                searchMode === 'contact' ? classes.searchModeButtonActive : ''
              }`}
              onClick={() => setSearchMode('contact')}
            >
              Contato
            </ButtonBase>
            <ButtonBase
              className={`${classes.searchModeButton} ${
                searchMode === 'message' ? classes.searchModeButtonActive : ''
              }`}
              onClick={() => setSearchMode('message')}
            >
              Mensagem
            </ButtonBase>
          </div>

          <div className={classes.tagsFilterWrap}>
            <Autocomplete
              multiple
              size="small"
              options={tags}
              value={selectedTags}
              onChange={(_e, value) => setSelectedTags(value)}
              getOptionLabel={(option) => option?.name || ''}
              limitTags={2}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id || index}
                    className={classes.tagChip}
                    style={{
                      backgroundColor: option.color ? `${option.color}22` : '#e2e8f0',
                      color: option.color || '#334155',
                    }}
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Filtrar por tags" />
              )}
            />
          </div>

          <Typography className={classes.searchMetaText}>
            {searchParam.length >= 2 ? (
              <>
                Buscando em {searchMode === 'message' ? 'mensagens' : 'contatos'} por{' '}
                <span className={classes.searchStrong}>{searchParam}</span>
                {selectedTags.length > 0 ? ` • ${selectedTags.length} tag(s)` : ''}
              </>
            ) : searchMode === 'message' ? (
              'Digite no minimo 2 caracteres para buscar dentro das mensagens'
            ) : (
              'Digite no minimo 2 caracteres para buscar em contatos'
            )}
          </Typography>
        </div>

        <div className={classes.bodyContainer}>
          {loading ? (
            <div className={classes.stateCard}>
              <CircularProgress size={32} />
              <Typography className={classes.loadingLabel}>Buscando conversas...</Typography>
            </div>
          ) : searchParam.length < 2 && selectedTags.length === 0 ? (
            <div className={classes.stateCard}>
              <SearchIcon className={classes.searchHintIcon} />
              <Typography className={classes.stateTitle}>Comece digitando</Typography>
              <Typography className={classes.stateText}>
                {searchMode === 'message'
                  ? 'Use um trecho da mensagem para filtrar resultados'
                  : 'Use nome, numero ou atendente para filtrar resultados'}
              </Typography>
            </div>
          ) : tickets.length === 0 ? (
            <div className={classes.stateCard}>
              <SearchIcon className={classes.searchHintIcon} />
              <Typography className={classes.stateTitle}>Nenhuma conversa encontrada</Typography>
              <Typography className={classes.stateText}>
                Tente ajustar o termo e buscar novamente
              </Typography>
            </div>
          ) : (
            <List className={classes.resultsList}>
              {tickets.map((ticket, index) => (
                <React.Fragment key={ticket.id}>
                  <ListItem className={classes.listItem}>
                    <button
                      type="button"
                      className={classes.listButton}
                      onClick={() => handleSelectTicket(ticket)}
                    >
                      <div className={classes.itemRow}>
                        <ListItemAvatar>
                          <Avatar className={classes.avatar} src={ticket.contact?.urlPicture}>
                            {ticket.contact?.name?.charAt(0).toUpperCase() || '?'}
                          </Avatar>
                        </ListItemAvatar>
                        <div className={classes.contentWrap}>
                          <div className={classes.nameLine}>
                            <Typography className={classes.contactName}>
                              {ticket.contact?.name || 'Sem nome'}
                            </Typography>
                            <Typography className={classes.ticketId}>#{ticket.id}</Typography>
                          </div>
                          <Typography className={classes.lastMessage} component="div">
                            {getMatchingMessagePreview(ticket)}
                          </Typography>
                          <div className={classes.ticketInfo}>
                            <Chip
                              icon={getChannelIcon(ticket.channel)}
                              label={ticket.channel || 'chat'}
                              size="small"
                              className={classes.channelChip}
                            />
                            <Chip
                              label={getStatusLabel(ticket.status)}
                              size="small"
                              className={classes.statusChip}
                              style={{
                                ...getStatusColor(ticket.status),
                                border: `1px solid ${getStatusBorderColor(ticket.status)}`,
                              }}
                            />
                            {ticket.queue && (
                              <Chip
                                label={ticket.queue.name}
                                size="small"
                                className={classes.queueChip}
                                style={{
                                  backgroundColor: `${ticket.queue.color}20`,
                                  color: ticket.queue.color,
                                  border: `1px solid ${ticket.queue.color}55`,
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </ListItem>
                  {index < tickets.length - 1 && <Divider className={classes.divider} />}
                </React.Fragment>
              ))}
            </List>
          )}
          {!loading && searchParam.length >= 2 && tickets.length > 0 && (
            <Typography className={classes.footerHint}>
              Clique em um resultado para abrir a conversa
            </Typography>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchTicketModal;
